import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mail, ShieldCheck, Loader2, ChevronDown, ChevronRight,
         CheckCircle2, XCircle, Clock, User, Building } from 'lucide-react';

/**
 * Видно ТОЛЬКО супер-админу. Сворачиваемая панель со списком всех
 * приглашённых на тендер участников и логом отправки писем-приглашений.
 */
export default function AdminInvitationsLog({ tenderId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/tenders/${tenderId}/invitations`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  useEffect(() => {
    if (open && !data) load();
  }, [open, data, load]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('ru-RU') : '—';

  return (
    <div className="mt-3 pt-3 border-t border-violet-200" data-testid={`admin-invitations-${tenderId}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-violet-50 hover:bg-violet-100 border border-violet-200 transition-colors"
        data-testid={`admin-invitations-toggle-${tenderId}`}
      >
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-violet-800">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <ShieldCheck className="w-3.5 h-3.5" />
          Приглашения (super-admin)
          {data && (
            <Badge variant="outline" className="ml-1 text-[10px] rounded-sm bg-white border-violet-300 text-violet-700 font-normal">
              {data.summary.total_invited} участников · {data.summary.sent} отпр. · {data.summary.failed} ошибок
            </Badge>
          )}
        </span>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-600" />}
      </button>

      {open && data && (
        <div className="mt-2 border border-violet-200 rounded-sm bg-white overflow-hidden">
          {/* Сводка */}
          <div className="px-3 py-2 bg-gradient-to-r from-violet-50 to-violet-50/40 border-b border-violet-100 grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
            <SummaryItem label="Всего приглашено" value={data.summary.total_invited} />
            <SummaryItem label="С аккаунтом" value={data.summary.with_account} />
            <SummaryItem label="Письма ОК" value={data.summary.sent} icon="ok" />
            <SummaryItem label="Ошибки" value={data.summary.failed} icon="err" />
            <SummaryItem label="Без лога" value={data.summary.no_log} />
          </div>

          {/* Таблица */}
          <div className="divide-y divide-slate-100">
            <div className="px-3 py-1.5 bg-slate-50 grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <div className="col-span-4">Поставщик</div>
              <div className="col-span-2">Аккаунт</div>
              <div className="col-span-3">Письмо</div>
              <div className="col-span-2">Заявка</div>
              <div className="col-span-1 text-right">⋮</div>
            </div>

            {data.rows.length === 0 && (
              <div className="px-3 py-6 text-center text-slate-400 text-xs">
                Никто не приглашён.
              </div>
            )}

            {data.rows.map(r => {
              const isExp = expandedEmail === r.email;
              return (
                <div key={r.email} className="text-[11px]">
                  <div className="px-3 py-1.5 grid grid-cols-12 gap-2 items-start hover:bg-slate-50">
                    {/* Поставщик */}
                    <div className="col-span-4 min-w-0">
                      <div className="font-semibold text-slate-800 truncate flex items-center gap-1">
                        <Building className="w-3 h-3 text-slate-400 shrink-0" />
                        {r.company_name || <span className="italic text-slate-400">без названия</span>}
                        {r.lang_code && (
                          <span className={`text-[9px] font-bold px-1 rounded ${r.lang_code === 'ENG' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                            {r.lang_code}
                          </span>
                        )}
                      </div>
                      <div className="text-slate-500 truncate text-[10px]">{r.email}</div>
                      {r.contact_name && (
                        <div className="text-slate-400 text-[10px] truncate flex items-center gap-1">
                          <User className="w-2.5 h-2.5" /> {r.contact_name}
                        </div>
                      )}
                    </div>

                    {/* Account */}
                    <div className="col-span-2">
                      {r.has_account ? (
                        <Badge variant="outline" className="text-[9px] rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> зарегистрирован
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] rounded-sm bg-amber-50 text-amber-700 border-amber-200">
                          <Clock className="w-2.5 h-2.5 mr-0.5" /> ожидает регистрации
                        </Badge>
                      )}
                    </div>

                    {/* Email log */}
                    <div className="col-span-3">
                      {!r.last_send_status ? (
                        <Badge variant="outline" className="text-[9px] rounded-sm bg-slate-50 text-slate-500 border-slate-200">
                          <Mail className="w-2.5 h-2.5 mr-0.5" /> нет попыток
                        </Badge>
                      ) : r.last_send_status === 'sent' ? (
                        <div>
                          <Badge variant="outline" className="text-[9px] rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> отправлено
                          </Badge>
                          <div className="text-[9px] text-slate-400 mt-0.5">
                            {fmt(r.last_send_at)} · попыток: {r.last_send_attempts || 1}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Badge variant="outline" className="text-[9px] rounded-sm bg-rose-50 text-rose-700 border-rose-200">
                            <XCircle className="w-2.5 h-2.5 mr-0.5" /> ошибка
                          </Badge>
                          <div className="text-[9px] text-rose-600 mt-0.5 truncate" title={r.last_send_error}>
                            {fmt(r.last_send_at)} · {r.last_send_error}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Заявка */}
                    <div className="col-span-2">
                      {r.application_status === 'accepted' ? (
                        <Badge variant="outline" className="text-[9px] rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">принята</Badge>
                      ) : r.application_status === 'rejected' ? (
                        <Badge variant="outline" className="text-[9px] rounded-sm bg-rose-50 text-rose-700 border-rose-200">отклонена</Badge>
                      ) : r.application_status === 'pending' ? (
                        <Badge variant="outline" className="text-[9px] rounded-sm bg-amber-50 text-amber-700 border-amber-200">ожидает</Badge>
                      ) : (
                        <span className="text-slate-400 text-[10px]">не подавал</span>
                      )}
                    </div>

                    {/* Expand */}
                    <div className="col-span-1 text-right">
                      {(r.history || []).length > 0 && (
                        <button
                          type="button"
                          onClick={() => setExpandedEmail(isExp ? null : r.email)}
                          className="text-violet-600 hover:bg-violet-50 px-1.5 py-0.5 rounded text-[10px]"
                          data-testid={`admin-invitation-expand-${r.email}`}
                          title="История попыток"
                        >
                          {isExp ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          {r.history.length}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* History expanded */}
                  {isExp && (r.history || []).length > 0 && (
                    <div className="bg-slate-50 px-3 py-2 border-t border-slate-100">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                        Все попытки отправки ({r.history.length})
                      </div>
                      <div className="space-y-1">
                        {r.history.map((h, i) => (
                          <div key={h.id || i} className="text-[10px] grid grid-cols-12 gap-2 items-center px-2 py-1 bg-white rounded-sm border border-slate-100">
                            <div className="col-span-3 text-slate-500">{fmt(h.created_at)}</div>
                            <div className="col-span-2">
                              {h.status === 'sent' ? (
                                <span className="text-emerald-700 font-semibold">✓ sent</span>
                              ) : (
                                <span className="text-rose-700 font-semibold">✗ failed</span>
                              )}
                            </div>
                            <div className="col-span-1 text-slate-500">×{h.attempts || 1}</div>
                            <div className="col-span-6 text-slate-600 truncate" title={h.error || ''}>{h.error || '—'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              Опубликован: {fmt(data.published_at)} · Статус: {data.status}
            </span>
            <Button size="sm" variant="ghost" onClick={() => { setData(null); load(); }}
              className="h-6 text-[10px] text-violet-700 hover:bg-violet-100">
              Обновить
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryItem({ label, value, icon }) {
  const cls = icon === 'ok' ? 'text-emerald-700' : icon === 'err' ? 'text-rose-700' : 'text-slate-700';
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`font-bold text-sm ${cls}`}>{value}</span>
    </div>
  );
}
